import type { ApolloError, FetchResult } from '@apollo/client';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { AllergyRecord } from '@models/allergy';
import { AppointmentRecord } from '@models/appointment';
import { DiagnosticReportRecord } from '@models/diagnostic-report';
import { EncounterRecord } from '@models/encounter';
import { FamilyHistoryRecord } from '@models/family-history';
import { GeneticInformationObservationRecord } from '@models/genetic-information';
import { ImagingStudyRecord } from '@models/imaging-study';
import { ImmunizationRecord } from '@models/immunization';
import { InsurancePlan } from '@models/insurance';
import { LabResultObservationRecord } from '@models/lab-result';
import { LegalDirectiveRecord } from '@models/legal-directive';
import { MedicalConditionRecord } from '@models/medical-condition';
import { MedicationStatementRecord } from '@models/medication';
import { PatientRecord } from '@models/patient';
import { ProcedureRecord } from '@models/procedure';
import { RelatedPersonRecord } from '@models/related-person';
import { VitalSignObservationRecord } from '@models/vital-sign';

import type {
  GetEmergencyAccessRecordsQueryVariables,
  GetPatientHealthSummaryRecordsQueryVariables,
  GetPatientTimelineRecordsQueryVariables,
  PostResourceAssociationMutation,
  PostResourceAssociationMutationVariables,
  RemoveResourceAssociationMutation,
  RemoveResourceAssociationMutationVariables,
} from 'generated/patient-record.generated';
import {
  useGetAllResourceModelsQuery,
  useGetEmergencyAccessRecordsLazyQuery,
  useGetLastRecordUpdateQuery,
  useGetMostRecentRecordsQuery,
  useGetPatientHealthSummaryRecordsQuery,
  useGetPatientTimelineRecordsQuery,
  usePostResourceAssociationMutation,
  useRemoveResourceAssociationMutation,
} from 'generated/patient-record.generated';
import type { AllPatientRecordsModel, CurationStatus } from 'generated/types';
import { useGetVitallPatientRecordMetaQuery } from 'generated/vitall-patient.generated';

export type PatientRecords = {
  allergy: AllergyRecord[];
  appointment: AppointmentRecord[];
  condition: MedicalConditionRecord[];
  diagnostic_report: DiagnosticReportRecord[];
  imaging_report: DiagnosticReportRecord[];
  lab_report: DiagnosticReportRecord[];
  emergency_visits: EncounterRecord[];
  family_member_history: FamilyHistoryRecord[];
  genetic_information_observation: GeneticInformationObservationRecord[];
  hospitalizations: EncounterRecord[];
  imaging_study: ImagingStudyRecord[];
  immunization: ImmunizationRecord[];
  laboratory_observation: LabResultObservationRecord[];
  body_observation: VitalSignObservationRecord[];
  medication_statement: MedicationStatementRecord[];
  procedure: ProcedureRecord[];
  vital_signs_observation: VitalSignObservationRecord[];
  legal_directives: LegalDirectiveRecord[];
  related_persons: RelatedPersonRecord[];
  coverage: InsurancePlan[];
  patient: PatientRecord | null;
  blood_type: LabResultObservationRecord | null;
  weight_body_observation: VitalSignObservationRecord | null;
  height_body_observation: VitalSignObservationRecord | null;
};

const mapPatientRecords: (
  data?: Partial<AllPatientRecordsModel>
) => PatientRecords = (data?: Partial<AllPatientRecordsModel>) => {
  const allergies = (data?.allergy ?? []).map((a) => new AllergyRecord(a));
  const appointments = (data?.appointment ?? []).map(
    (a) => new AppointmentRecord(a)
  );
  const conditions = (data?.condition ?? []).map(
    (c) => new MedicalConditionRecord(c)
  );
  const diagnosticReports = (data?.diagnostic_report ?? []).map(
    (d) => new DiagnosticReportRecord(d)
  );
  const imagingReports = (data?.imaging_report ?? []).map(
    (d) => new DiagnosticReportRecord(d)
  );
  const labReports = (data?.lab_report ?? []).map(
    (d) => new DiagnosticReportRecord(d)
  );
  const emergencyVisits = (data?.emergency_visits_encounter ?? []).map(
    (e) => new EncounterRecord(e)
  );
  const familyHistory = (data?.family_member_history ?? []).map(
    (f) => new FamilyHistoryRecord(f)
  );
  const geneticInfo = (data?.genetic_information_observation ?? []).map(
    (g) => new GeneticInformationObservationRecord(g)
  );
  const hospitalizations = (data?.hospitalizations_encounter ?? []).map(
    (h) => new EncounterRecord(h)
  );
  const imagingStudies = (data?.imaging_study ?? []).map(
    (i) => new ImagingStudyRecord(i)
  );
  const immunizations = (data?.immunization ?? []).map(
    (i) => new ImmunizationRecord(i)
  );
  const labObservations = (data?.laboratory_observation ?? []).map(
    (l) => new LabResultObservationRecord(l)
  );
  const body_observation = (data?.body_observation ?? []).map(
    (l) => new VitalSignObservationRecord(l)
  );
  const legalDirectives = (data?.legal_directives ?? []).map(
    (l) => new LegalDirectiveRecord(l)
  );
  const relatedPersons = (data?.related_person ?? []).map(
    (l) => new RelatedPersonRecord(l)
  );
  const medications = (data?.medication_statement ?? []).map(
    (m) => new MedicationStatementRecord(m)
  );
  const surgeries = (data?.procedure ?? []).map((p) => new ProcedureRecord(p));
  const vitalSigns = (data?.vital_signs_observation ?? []).map(
    (v) => new VitalSignObservationRecord(v)
  );
  const coverage = (data?.coverage ?? []).map((v) => new InsurancePlan(v));
  const patient = data?.patient ? new PatientRecord(data.patient) : null;
  const blood_type = data?.blood_type
    ? new LabResultObservationRecord(data.blood_type)
    : null;
  const height_body_observation = data?.height_body_observation
    ? new VitalSignObservationRecord(data.height_body_observation)
    : null;
  const weight_body_observation = data?.weight_body_observation
    ? new VitalSignObservationRecord(data.weight_body_observation)
    : null;
  return {
    allergy: allergies,
    appointment: appointments,
    condition: conditions,
    diagnostic_report: diagnosticReports,
    imaging_report: imagingReports,
    lab_report: labReports,
    emergency_visits: emergencyVisits,
    family_member_history: familyHistory,
    genetic_information_observation: geneticInfo,
    hospitalizations,
    imaging_study: imagingStudies,
    immunization: immunizations,
    laboratory_observation: labObservations,
    medication_statement: medications,
    procedure: surgeries,
    vital_signs_observation: vitalSigns,
    related_persons: relatedPersons,
    legal_directives: legalDirectives,
    coverage,
    patient,
    blood_type,
    body_observation,
    height_body_observation,
    weight_body_observation,
  };
};

export const useGetPatientHealthSummaryRecords = (
  params?: Omit<GetPatientHealthSummaryRecordsQueryVariables, 'input'>
) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetPatientHealthSummaryRecordsQuery({
    variables: {
      input: {
        patient: patient_id,
      },
      ...params,
    },
    skip: !patient_id || !params,
    fetchPolicy: 'no-cache',
  });
  handleError(response.error);

  return {
    ...response,
    data: {
      ...mapPatientRecords(response.data?.getPatientHealthSummaryRecords),
      care_team: response.data?.getPatientHealthSummaryRecords.care_team,
    },
  };
};

export const useGetPatientTimelineRecords = (
  params?: Omit<GetPatientTimelineRecordsQueryVariables, 'input'>
) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetPatientTimelineRecordsQuery({
    variables: {
      input: {
        patient: patient_id,
      },
      ...params,
    },
    skip: !patient_id || !params,
    fetchPolicy: 'no-cache',
  });
  handleError(response.error);

  return {
    ...response,
    data: mapPatientRecords(response.data?.getPatientTimelineRecords),
  };
};

export const useGetAllResourceModels = (params: { status: CurationStatus }) => {
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetAllResourceModelsQuery({
    variables: {
      input: {
        patient_id,
        status: params.status,
      },
    },
    skip: !patient_id || !params.status,
    errorPolicy: 'none',
  });
  return response;
};

export const useGetMostRecentRecords = () => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetMostRecentRecordsQuery({
    variables: {
      input: {
        patient_id,
      },
    },
    skip: !patient_id,
  });
  handleError(response.error);
  return response;
};

export function useEmergencyLogin(): [
  (
    params: GetEmergencyAccessRecordsQueryVariables
  ) => Promise<FetchResult<PatientRecords> & { error?: ApolloError }>,
  FetchResult<PatientRecords> & { error?: ApolloError },
] {
  const [query, response] = useGetEmergencyAccessRecordsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  async function emergencyLogin(
    params: GetEmergencyAccessRecordsQueryVariables
  ) {
    const response = await query({
      variables: params,
      fetchPolicy: 'no-cache',
    });
    return {
      ...response,
      data: mapPatientRecords(response.data?.getPatientEmergencyAccessRecords),
    };
  }

  return [
    emergencyLogin,
    {
      ...response,
      data: mapPatientRecords(response.data?.getPatientEmergencyAccessRecords),
    },
  ];
}

export const useGetLastRecordUpdate = (skip?: boolean) => {
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetLastRecordUpdateQuery({
    variables: { input: { patient_id } },
    skip: !patient_id || skip,
  });
  return response;
};

export function useAssociateRelatedRecord(): [
  (
    params: PostResourceAssociationMutationVariables
  ) => Promise<FetchResult<PostResourceAssociationMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    usePostResourceAssociationMutation();
  const loading = baseLoading;

  async function createRelatedRecord(
    params: PostResourceAssociationMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    return response;
  }
  return [createRelatedRecord, loading];
}

export function useDisassociateRelatedRecord(): [
  (
    params: RemoveResourceAssociationMutationVariables
  ) => Promise<FetchResult<RemoveResourceAssociationMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useRemoveResourceAssociationMutation();
  const loading = baseLoading;

  async function deleteRelatedRecord(
    params: RemoveResourceAssociationMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    return response;
  }
  return [deleteRelatedRecord, loading];
}

export const useGetVitallPatientRecordMeta = (params?: {
  user_id?: string;
}) => {
  const {
    state: { activeProfile },
  } = useSession();
  const user_id = params?.user_id ?? activeProfile?.user_id;
  const response = useGetVitallPatientRecordMetaQuery({
    variables: {
      input: {
        user_id,
      },
    },
    skip: !user_id,
  });
  return { ...response, data: response.data?.getVitallPatientRecord };
};
